// icons/ — the framework's small, restrained icon vocabulary.
//
// Voice rule: line-only SVG, 1.25px stroke, monochrome, inherits color
// via `currentColor`. 28px viewbox by default; consumers may resize via
// width/height or a `size` prop. No fills (except for explicitly dot-
// like marks). Geometric/abstract — diagram fragments, not pictograms.
//
// The icons are diagrammatic shorthand for framework concepts, not
// decoration. Use them where they earn their place; leave them off
// where typographic restraint is doing the work.

import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & {
  size?: number;
};

/** DoubleHelixIcon — two intertwined sine waves marking the Voyage's
 *  twin-row architecture (Medium lyrical / Substack leverage-points
 *  reading the same twelve days from different angles). */
export function DoubleHelixIcon({ size = 28, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 28 28"
      stroke="currentColor"
      fill="none"
      strokeWidth={1.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...rest}
    >
      {/* Strand A — sine wave, offset down */}
      <path d="M 3 9 Q 7 4, 11 9 T 19 9 T 27 9" />
      {/* Strand B — inverted sine wave, mirrored */}
      <path d="M 3 19 Q 7 24, 11 19 T 19 19 T 27 19" />
      {/* Cross-rungs at the helix waists */}
      <line x1="7" y1="9" x2="7" y2="19" opacity="0.5" />
      <line x1="15" y1="9" x2="15" y2="19" opacity="0.5" />
      <line x1="23" y1="9" x2="23" y2="19" opacity="0.5" />
    </svg>
  );
}

function Base({ size = 28, children, ...rest }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 28 28"
      stroke="currentColor"
      fill="none"
      strokeWidth={1.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...rest}
    >
      {children}
    </svg>
  );
}

/* ── Architectural counter set ──────────────────────────── */

/** SovereigntyIcon — A ≤ E. Inner circle bounded by outer circle.
 *  References the framework's founding constraint diagram. */
export function SovereigntyIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={14} cy={14} r={11} />
      <circle cx={14} cy={14} r={5} />
    </Base>
  );
}

/** WitnessIcon — six dots around an empty center. Many witnesses
 *  hold what no single one of them captures. */
export function WitnessIcon(props: IconProps) {
  const dots: [number, number][] = [
    [14, 5],
    [22, 9.5],
    [22, 18.5],
    [14, 23],
    [6, 18.5],
    [6, 9.5],
  ];
  return (
    <Base {...props}>
      {dots.map(([cx, cy], i) => (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r={1.6}
          fill="currentColor"
          stroke="none"
        />
      ))}
    </Base>
  );
}

/** RepairIcon — a horizontal mend with three vertical stitches.
 *  The line is broken; the stitches close it. */
export function RepairIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 14 L25 14" />
      <path d="M10 11 L10 17" />
      <path d="M14 10.5 L14 17.5" />
      <path d="M18 11 L18 17" />
    </Base>
  );
}

/** HoneycombIcon — single hexagon. Distributed load; each cell
 *  bears a share. Referenced verbatim in Six Seven and the
 *  Installed Watcher synthesis. */
export function HoneycombIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M14 3 L23 8 L23 18 L14 23 L5 18 L5 8 Z" />
    </Base>
  );
}

/* ── Layer markers (research/program shorthand) ─────────── */

/** FrameworkLayerIcon — three nested rectangles (framework layer). */
export function FrameworkLayerIcon(props: IconProps) {
  return (
    <Base {...props}>
      <rect x={3} y={5} width={22} height={4} />
      <rect x={3} y={12} width={22} height={4} />
      <rect x={3} y={19} width={22} height={4} />
    </Base>
  );
}

/** ResearchLayerIcon — a magnifier-style circle + handle. */
export function ResearchLayerIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={11} cy={11} r={7} />
      <path d="M16 16 L23 23" />
    </Base>
  );
}

/** OperationalLayerIcon — a chevron-style flow-stack. */
export function OperationalLayerIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M5 7 L14 11 L23 7" />
      <path d="M5 14 L14 18 L23 14" />
      <path d="M5 21 L14 25 L23 21" />
    </Base>
  );
}

/* ── Canonical Stack — one icon per layer ───────────────── */

/** DigitalGravityIcon — a doorway arch. The public-intellectual
 *  framework as the doorway readers arrive at first. */
export function DigitalGravityIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M5 24 L5 12 Q14 4, 23 12 L23 24" />
      <path d="M2 24 L26 24" />
    </Base>
  );
}

/** RtiIcon — two nodes joined by a beam. Relational + structural;
 *  the load-bearing infrastructure of trust between parties. */
export function RtiIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={6} cy={14} r={3} />
      <circle cx={22} cy={14} r={3} />
      <path d="M9 14 L19 14" />
      <path d="M6 18 L6 23" />
      <path d="M22 18 L22 23" />
    </Base>
  );
}

/** TrustForceIcon — two arrows converging at center. The dyadic /
 *  relational formation model: mutual cost, mutual presence. */
export function TrustForceIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 14 L13 14" />
      <path d="M10 11 L13 14 L10 17" />
      <path d="M25 14 L15 14" />
      <path d="M18 11 L15 14 L18 17" />
    </Base>
  );
}

/** PercolationIcon — a 3×3 lattice with selected edges showing
 *  network connectivity. The Ostrom-percolation reading. */
export function PercolationIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M6 6 L14 6 L22 6" />
      <path d="M6 14 L14 14 L22 14" />
      <path d="M6 6 L6 22" />
      <path d="M22 6 L22 22" />
      <path d="M14 14 L14 22" />
      <circle cx={6} cy={6} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={14} cy={6} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={22} cy={6} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={6} cy={14} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={14} cy={14} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={22} cy={14} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={6} cy={22} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={14} cy={22} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={22} cy={22} r={1.6} fill="currentColor" stroke="none" />
    </Base>
  );
}

/** ReconciliationIcon — a bisected circle with a vertical seam.
 *  Two halves rejoined; the betrayal-metabolism model. Distinct
 *  from RepairIcon (horizontal mend) by axis. */
export function ReconciliationIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={14} cy={14} r={10} />
      <path d="M14 6 L14 22" />
      <path d="M11 9 L17 9" />
      <path d="M11 14 L17 14" />
      <path d="M11 19 L17 19" />
    </Base>
  );
}

/** KtpIcon — a bounded circle with an upward chevron inside. The
 *  trajectory of earned autonomy contained within constraint. */
export function KtpIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={14} cy={14} r={10} />
      <path d="M9 18 L14 12 L19 18" />
      <path d="M14 12 L14 8" />
    </Base>
  );
}

/** TamedAutonomyIcon — a tethered agent. A small filled dot bound
 *  to an anchor by a single line. Applied AI-agent governance. */
export function TamedAutonomyIcon(props: IconProps) {
  return (
    <Base {...props}>
      <rect x={3} y={3} width={6} height={6} />
      <path d="M9 9 L18 18" />
      <circle cx={20} cy={20} r={3} fill="currentColor" stroke="none" />
    </Base>
  );
}

/* ── DCTI stages — one icon per stage of the felt sequence ── */

/** CaptureIcon — cross-hair: attention seized from four
 *  directions onto a single point. The thumb-stop. */
export function CaptureIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={14} cy={14} r={3.5} />
      <path d="M3 14 L8 14" />
      <path d="M20 14 L25 14" />
      <path d="M14 3 L14 8" />
      <path d="M14 20 L14 25" />
    </Base>
  );
}

/** TaggingIcon — a pennant flag. The affective tag attached to a
 *  signal before semantic processing arrives. */
export function TaggingIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M5 4 L5 24" />
      <path d="M5 6 L20 6 L24 10 L20 14 L5 14 Z" />
    </Base>
  );
}

/** EncodingIcon — three parallel grooves, the middle one heavier.
 *  The path through grass; first-frame as default activation. */
export function EncodingIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 7 L25 7" strokeWidth={0.9} />
      <path d="M3 14 L25 14" strokeWidth={2.4} />
      <path d="M3 21 L25 21" strokeWidth={0.9} />
    </Base>
  );
}

/** ValuationIcon — a tilted scale beam with weights at each end.
 *  The loaded scale; deliberation operating on pre-weighted inputs. */
export function ValuationIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M14 4 L14 8" />
      <path d="M4 11 L24 17" />
      <rect x={2} y={11} width={6} height={3} />
      <rect x={20} y={17} width={6} height={3} />
    </Base>
  );
}

/** CommitmentIcon — an arrow crossing a vertical threshold.
 *  Behavior taken; the act crossing the line. */
export function CommitmentIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M14 3 L14 25" strokeDasharray="2 2" />
      <path d="M3 14 L23 14" />
      <path d="M20 11 L23 14 L20 17" />
    </Base>
  );
}

/** ResidueIcon — a dashed line (the correction process) with a
 *  solid dot remaining at center. The residue that outlasts the
 *  correction. */
export function ResidueIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 14 L9 14" strokeDasharray="2 2" />
      <circle cx={14} cy={14} r={2.5} fill="currentColor" stroke="none" />
      <path d="M19 14 L25 14" strokeDasharray="2 2" />
    </Base>
  );
}

/* ── Canon hub artifacts ────────────────────────────────── */

/** SynthesisIcon — past and future converging at a present center.
 *  Lineage and Launch as one synthesis. */
export function SynthesisIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 14 L10 14" />
      <path d="M25 14 L18 14" />
      <circle cx={14} cy={14} r={3} />
    </Base>
  );
}

/** ClaimsIcon — a maturity-ladder staircase. L1 to L7, ascending. */
export function ClaimsIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 24 L3 19 L9 19 L9 14 L15 14 L15 9 L21 9 L21 4 L25 4" />
    </Base>
  );
}

/** LimitsIcon — two parallel verticals. The boundary marks
 *  between which the framework sits. */
export function LimitsIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M9 4 L9 24" />
      <path d="M19 4 L19 24" />
      <path d="M9 14 L19 14" />
    </Base>
  );
}

/** GlossaryIcon — a colon and a definition line. Term: definition. */
export function GlossaryIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={6} cy={10} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={6} cy={18} r={1.6} fill="currentColor" stroke="none" />
      <path d="M11 10 L25 10" />
      <path d="M11 14 L21 14" />
      <path d="M11 18 L25 18" />
    </Base>
  );
}

/** EthicsIcon — a balance beam on a stand. The house rule. */
export function EthicsIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M4 11 L24 11" />
      <path d="M14 11 L14 22" />
      <path d="M9 22 L19 22" />
      <path d="M5 8 L5 14" />
      <path d="M23 8 L23 14" />
    </Base>
  );
}

/** StackIcon — five horizontal layers. The Canonical Stack. */
export function StackIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 5 L25 5" />
      <path d="M3 10 L25 10" />
      <path d="M3 14 L25 14" />
      <path d="M3 19 L25 19" />
      <path d="M3 24 L25 24" />
    </Base>
  );
}

/* ── Research hub navigation ────────────────────────────── */

/** RoadmapIcon — three nodes connected by a line. Stages along
 *  a path. */
export function RoadmapIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={5} cy={14} r={2.4} fill="currentColor" stroke="none" />
      <circle cx={14} cy={14} r={2.4} fill="currentColor" stroke="none" />
      <circle cx={23} cy={14} r={2.4} fill="currentColor" stroke="none" />
      <path d="M7.5 14 L11.5 14" />
      <path d="M16.5 14 L20.5 14" />
    </Base>
  );
}

/** PapersIcon — a document with three text lines. */
export function PapersIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M6 3 L6 25 L22 25 L22 3 Z" />
      <path d="M9 9 L19 9" />
      <path d="M9 14 L19 14" />
      <path d="M9 19 L16 19" />
    </Base>
  );
}

/** CartographyIcon — three topographic contour lines. */
export function CartographyIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 8 Q9 4 14 8 T25 8" />
      <path d="M3 14 Q9 10 14 14 T25 14" />
      <path d="M3 20 Q9 16 14 20 T25 20" />
    </Base>
  );
}

/** FalsificationIcon — a boxed X. A claim presented for testing,
 *  with the failure result already legible. */
export function FalsificationIcon(props: IconProps) {
  return (
    <Base {...props}>
      <rect x={4} y={4} width={20} height={20} />
      <path d="M9 9 L19 19" />
      <path d="M19 9 L9 19" />
    </Base>
  );
}

/** CollaborateIcon — two heads with a connecting curve below.
 *  People-oriented. */
export function CollaborateIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={9} cy={10} r={3} />
      <circle cx={19} cy={10} r={3} />
      <path d="M3 22 Q9 17 14 22 Q19 17 25 22" />
    </Base>
  );
}

/** CognitiveIcon — a core with surrounding envelope and four
 *  radiating signals. Cognitive terrain. */
export function CognitiveIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={14} cy={14} r={9} />
      <circle cx={14} cy={14} r={3} />
      <path d="M14 5 L14 8" />
      <path d="M14 20 L14 23" />
      <path d="M5 14 L8 14" />
      <path d="M20 14 L23 14" />
    </Base>
  );
}

/* ── Cognitive hub artifacts ────────────────────────────── */

/** DctiIcon — a shield. The reader-side defensive instrument. */
export function DctiIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M14 3 L23 7 L23 15 Q23 21 14 25 Q5 21 5 15 L5 7 Z" />
      <path d="M11 13 L13 16 L18 11" />
    </Base>
  );
}

/** AcppIcon — a warning triangle with a centered alert mark.
 *  The threat surface, named so it can be recognized. */
export function AcppIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M14 4 L24 22 L4 22 Z" />
      <path d="M14 11 L14 17" />
      <circle cx={14} cy={20} r={1} fill="currentColor" stroke="none" />
    </Base>
  );
}

/** InstalledWatcherIcon — an eye. The installed gaze inside the
 *  target's cognition. */
export function InstalledWatcherIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 14 Q14 6 25 14 Q14 22 3 14 Z" />
      <circle cx={14} cy={14} r={3} />
      <circle cx={14} cy={14} r={1} fill="currentColor" stroke="none" />
    </Base>
  );
}

/** LadderIcon — two rails and five rungs. The defensive instrument:
 *  the succession you climb to reverse-engineer an influence attempt. */
export function LadderIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M10 3 L10 25" />
      <path d="M18 3 L18 25" />
      <path d="M10 7 L18 7" />
      <path d="M10 12 L18 12" />
      <path d="M10 16 L18 16" />
      <path d="M10 20 L18 20" />
    </Base>
  );
}

/* ── /implement ─────────────────────────────────────────── */

/** WorkloadIcon — a small filled square inside a larger outlined
 *  square. Pod inside host. */
export function WorkloadIcon(props: IconProps) {
  return (
    <Base {...props}>
      <rect x={4} y={4} width={20} height={20} />
      <rect
        x={11}
        y={11}
        width={6}
        height={6}
        fill="currentColor"
        stroke="none"
      />
    </Base>
  );
}

/** OracleIcon — three small triangles pointing at a center dot.
 *  Threshold of three independent attestations. */
export function OracleIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={14} cy={14} r={2} fill="currentColor" stroke="none" />
      <path d="M11 6 L14 10 L17 6 Z" />
      <path d="M22 11 L18 14 L22 17 Z" />
      <path d="M11 22 L14 18 L17 22 Z" />
    </Base>
  );
}

/** InstallIcon — a downward arrow ending on a horizontal surface. */
export function InstallIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M14 4 L14 18" />
      <path d="M9 13 L14 18 L19 13" />
      <path d="M4 23 L24 23" />
    </Base>
  );
}

/** DashboardIcon — three vertical bars of varying heights inside
 *  a frame. Readout, not gauge. */
export function DashboardIcon(props: IconProps) {
  return (
    <Base {...props}>
      <rect x={4} y={4} width={20} height={20} />
      <path d="M9 19 L9 13" />
      <path d="M14 19 L14 9" />
      <path d="M19 19 L19 15" />
    </Base>
  );
}

/** ConformanceIcon — a checkmark inside a square frame. The test
 *  badge that returns pass/fail. */
export function ConformanceIcon(props: IconProps) {
  return (
    <Base {...props}>
      <rect x={4} y={4} width={20} height={20} />
      <path d="M9 14 L13 18 L19 10" />
    </Base>
  );
}

/* ── /specs layers ──────────────────────────────────────── */

/** FoundationIcon — horizontal baseline with three short verticals
 *  descending below. The keel beneath the stack. */
export function FoundationIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 12 L25 12" />
      <path d="M8 12 L8 22" />
      <path d="M14 12 L14 22" />
      <path d="M20 12 L20 22" />
    </Base>
  );
}

/** TransportIcon — two parallel horizontals offset by a short
 *  diagonal. A packet jumping between zones. */
export function TransportIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 9 L13 9" />
      <path d="M13 9 L17 19" />
      <path d="M17 19 L25 19" />
      <path d="M14 6 L13 9 L16 10" />
      <path d="M14 22 L17 19 L20 22" />
    </Base>
  );
}

/* ── /research/cartography terrain ──────────────────────── */

/** RidgeIcon — a single peaked ridgeline with a contour underneath. */
export function RidgeIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 18 L14 6 L25 18" />
      <path d="M6 22 L14 14 L22 22" strokeOpacity={0.55} />
    </Base>
  );
}

/** ValleyIcon — an inverted ridge: two slopes meeting at a low
 *  point. */
export function ValleyIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 6 L14 18 L25 6" />
      <path d="M6 10 L14 18 L22 10" strokeOpacity={0.55} />
    </Base>
  );
}

/** WatershedIcon — a single line that branches at a node into two
 *  diverging directions. The divide. */
export function WatershedIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M14 4 L14 14" />
      <circle cx={14} cy={14} r={1.6} fill="currentColor" stroke="none" />
      <path d="M14 14 L6 22" />
      <path d="M14 14 L22 22" />
    </Base>
  );
}

/** FaultLineIcon — a horizontal line interrupted by a vertical
 *  offset. The fault. */
export function FaultLineIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 11 L13 11" />
      <path d="M13 11 L13 17" />
      <path d="M13 17 L25 17" />
    </Base>
  );
}

/* ── /learn concepts ────────────────────────────────────── */

/** VectorIdentityIcon — an arrow with a small dot at its tail.
 *  Identity as direction-from-history. */
export function VectorIdentityIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={5} cy={14} r={1.6} fill="currentColor" stroke="none" />
      <path d="M5 14 L23 14" />
      <path d="M19 10 L23 14 L19 18" />
    </Base>
  );
}

/** SilentVetoIcon — a circle with a single horizontal bar across
 *  its diameter. The prohibition without explanation. */
export function SilentVetoIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={14} cy={14} r={10} />
      <path d="M5 14 L23 14" strokeWidth={2} />
    </Base>
  );
}

/** ProofOfResilienceIcon — a wavy line crossing a horizontal
 *  threshold. Survived stress. */
export function ProofOfResilienceIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 14 L25 14" strokeDasharray="2 2" strokeOpacity={0.6} />
      <path d="M3 18 Q7 8 11 14 T19 14 T25 8" />
    </Base>
  );
}

/** MassCeilingIcon — a horizontal line above a dot, with a short
 *  vertical between. The bound. */
export function MassCeilingIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M5 7 L23 7" strokeWidth={2} />
      <path d="M14 7 L14 18" />
      <circle cx={14} cy={20} r={2.5} fill="currentColor" stroke="none" />
    </Base>
  );
}

/** ContextTensorIcon — a 3×3 grid of dots with the center filled.
 *  The seven-dimensional matrix abstracted. */
export function ContextTensorIcon(props: IconProps) {
  const positions: [number, number, boolean][] = [
    [7, 7, false],
    [14, 7, false],
    [21, 7, false],
    [7, 14, false],
    [14, 14, true],
    [21, 14, false],
    [7, 21, false],
    [14, 21, false],
    [21, 21, false],
  ];
  return (
    <Base {...props}>
      {positions.map(([cx, cy, filled], i) =>
        filled ? (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={1.8}
            fill="currentColor"
            stroke="none"
          />
        ) : (
          <circle key={i} cx={cx} cy={cy} r={1.5} />
        )
      )}
    </Base>
  );
}

/** DikwIcon — four horizontal lines stacked at growing widths. The
 *  Data → Information → Knowledge → Wisdom ladder. */
export function DikwIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M9 22 L19 22" />
      <path d="M7 17 L21 17" />
      <path d="M5 12 L23 12" />
      <path d="M3 7 L25 7" />
    </Base>
  );
}

/** LineageIcon — a chain of three small circles connected by short
 *  links. Trajectory. */
export function LineageIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={5} cy={14} r={2} />
      <path d="M7 14 L11 14" />
      <circle cx={14} cy={14} r={2.5} />
      <path d="M16.5 14 L20.5 14" />
      <circle cx={23} cy={14} r={2} />
    </Base>
  );
}

/* ── kinetic-trust SDK subsystems (Implement page) ──────── */

/** CryptoIcon — a key fragment with bit teeth. The crypto subsystem
 *  signs Trust Proofs; this is the one place the SDK speaks in the
 *  security register. */
export function CryptoIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={9} cy={14} r={4} />
      <path d="M13 14 L24 14" />
      <path d="M19 14 L19 18" />
      <path d="M22 14 L22 17" />
    </Base>
  );
}

/** FederationIcon — two interlocked rings. Cross-zone trust
 *  exchange between independent zones. */
export function FederationIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={10} cy={14} r={6} />
      <circle cx={18} cy={14} r={6} />
    </Base>
  );
}

/** MiddlewareIcon — three stacked bars with an arrow threading
 *  through. Middleware as a pass-through that touches each layer. */
export function MiddlewareIcon(props: IconProps) {
  return (
    <Base {...props}>
      <rect x={4} y={6} width={20} height={2.5} />
      <rect x={4} y={12.75} width={20} height={2.5} />
      <rect x={4} y={19.5} width={20} height={2.5} />
      <path d="M2 14 L26 14" strokeWidth={1.4} />
      <path d="M22 11 L26 14 L22 17" strokeWidth={1.4} />
    </Base>
  );
}

/** ShipwreckIcon — a tilted hull with two leaning masts above a wave
 *  baseline. The tilt is the diagnostic; a perfectly upright ship is
 *  not yet a wreck. Used on /research/shipwreck. */
export function ShipwreckIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Wave baseline — light hairline */}
      <path d="M2 22 Q6 20, 10 22 T18 22 T26 22" opacity={0.6} />
      {/* Hull — tilted right, sunken on the left */}
      <path d="M4 18 L24 16 L21 21 L7 21 Z" />
      {/* Foremast — tilted */}
      <line x1="9" y1="18" x2="11" y2="6" />
      {/* Mainmast — broken / leaning more */}
      <line x1="16" y1="17" x2="20" y2="9" />
      {/* Foremast crossarm */}
      <line x1="8" y1="10" x2="13" y2="9" opacity={0.7} />
    </Base>
  );
}

/** CosmologiesIcon — three concentric arcs of decreasing weight, each
 *  arc a different orientation. Read as nested cosmologies — different
 *  framings of the same center, sharing it without collapsing into
 *  each other. Used on /research/cosmologies. */
export function CosmologiesIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Outermost arc — top half */}
      <path d="M3 14 A11 11 0 0 1 25 14" />
      {/* Middle arc — bottom half, rotated counter */}
      <path d="M6 14 A8 8 0 0 0 22 14" opacity={0.75} />
      {/* Inner arc — small, top half */}
      <path d="M9 14 A5 5 0 0 1 19 14" opacity={0.55} />
      {/* Center mark — a dot the cosmologies share */}
      <circle cx="14" cy="14" r="1.2" fill="currentColor" stroke="none" />
    </Base>
  );
}

/** AttractorIcon — six dots arranged around a center, with short
 *  inward-pointing tick lines that imply convergence toward the
 *  centroid. Read as activation-space attractor: identity documents
 *  drawn into a tight region. Used on /research/identity-as-attractor. */
export function AttractorIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Center mark — the attractor centroid */}
      <circle cx="14" cy="14" r="1.6" fill="currentColor" stroke="none" />
      {/* Six surrounding dots — the converging documents */}
      <circle cx="14" cy="5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="22" cy="9.5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="22" cy="18.5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="14" cy="23" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="6" cy="18.5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="6" cy="9.5" r="1.4" fill="currentColor" stroke="none" />
      {/* Inward-pointing convergence ticks */}
      <line x1="14" y1="7" x2="14" y2="10" opacity={0.6} />
      <line x1="20" y1="10.5" x2="17" y2="12" opacity={0.6} />
      <line x1="20" y1="17.5" x2="17" y2="16" opacity={0.6} />
      <line x1="14" y1="21" x2="14" y2="18" opacity={0.6} />
      <line x1="8" y1="17.5" x2="11" y2="16" opacity={0.6} />
      <line x1="8" y1="10.5" x2="11" y2="12" opacity={0.6} />
    </Base>
  );
}

/** DryDockIcon — an upright hull with a single mast inside the frame
 *  of a dry dock (two walls and a floor). The pair to ShipwreckIcon:
 *  where the wreck sails out, the dock builds and repairs. Used on
 *  /learn/simulator. */
export function DryDockIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Dock walls — vertical hairlines */}
      <line x1="3" y1="11" x2="3" y2="22" opacity={0.7} />
      <line x1="25" y1="11" x2="25" y2="22" opacity={0.7} />
      {/* Dock floor */}
      <line x1="3" y1="22" x2="25" y2="22" />
      {/* Hull — upright trapezoid sitting on floor */}
      <path d="M8 22 L20 22 L18 17 L10 17 Z" />
      {/* Mast — straight up */}
      <line x1="14" y1="17" x2="14" y2="7" />
      {/* Crossarm */}
      <line x1="11" y1="9" x2="17" y2="9" opacity={0.7} />
    </Base>
  );
}

/* ── Dry Dock personas ───────────────────────────────────── */

/** NewArrivalIcon — a doorway with a small dot just inside. The
 *  newcomer who has cleared the gate but has no path behind them
 *  yet. IAL3-verified entrance, blank trajectory. */
export function NewArrivalIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Doorway frame */}
      <path d="M6 24 L6 8 Q14 4, 22 8 L22 24" />
      <path d="M3 24 L25 24" />
      {/* The just-arrived agent dot inside the threshold */}
      <circle cx={14} cy={18} r={2} fill="currentColor" stroke="none" />
    </Base>
  );
}

/** EstablishedIcon — a chevron trail ending at a filled node.
 *  Trajectory accumulating into a known agent; the substrate has
 *  watched this one for a while. */
export function EstablishedIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Three trailing position marks, dim → bright */}
      <circle cx={4} cy={14} r={1.2} fill="currentColor" stroke="none" opacity={0.35} />
      <circle cx={9} cy={14} r={1.5} fill="currentColor" stroke="none" opacity={0.6} />
      <circle cx={15} cy={14} r={1.8} fill="currentColor" stroke="none" opacity={0.85} />
      {/* Current node — the established agent */}
      <circle cx={22} cy={14} r={3} fill="currentColor" stroke="none" />
    </Base>
  );
}

/** CapturedTrajectoryIcon — a chevron trail interrupted by a fault
 *  line at the end. The trajectory is real; the identity that owns
 *  it now is not. The mismatch is the diagnostic. */
export function CapturedTrajectoryIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Trailing trajectory dots */}
      <circle cx={4} cy={14} r={1.2} fill="currentColor" stroke="none" opacity={0.5} />
      <circle cx={9} cy={14} r={1.5} fill="currentColor" stroke="none" opacity={0.75} />
      <circle cx={14} cy={14} r={1.8} fill="currentColor" stroke="none" />
      {/* Vertical break — the mismatch */}
      <path d="M18 6 L18 22" strokeDasharray="2 2" />
      {/* The masked taker on the far side */}
      <path d="M22 11 L22 17" />
      <path d="M20 11 L24 11" />
      <path d="M20 17 L24 17" />
    </Base>
  );
}

/** IceCubeIcon — a square crystal with two short interior facet lines
 *  and a single highlight glint. The Cold Drop persona: dropped in
 *  cold, no acquaintance with the substrate. Heavy, useless, frozen
 *  in place by surrounding vetoes. */
export function IceCubeIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Cube outer face */}
      <path d="M5 8 L23 8 L23 24 L5 24 Z" />
      {/* Top facet (the lid you can see at an angle) */}
      <path d="M5 8 L9 4 L27 4 L23 8" />
      {/* Side facet (right) */}
      <path d="M23 8 L27 4 L27 20 L23 24" />
      {/* Interior facet line */}
      <path d="M9 4 L9 20 L5 24" opacity={0.7} />
      <path d="M9 20 L27 20" opacity={0.7} />
      {/* Glint */}
      <path d="M11 12 L13 12" opacity={0.8} />
      <path d="M11 12 L11 14" opacity={0.8} />
    </Base>
  );
}

/* ── Dry Dock action set ─────────────────────────────────── */

/** DeleteOwnFileIcon — file outline with a horizontal strike. The
 *  permitted destruction case: the substrate is not anti-action; a
 *  workspace file the agent created is bounded territory. */
export function DeleteOwnFileIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* File body with dog-eared corner */}
      <path d="M7 5 L17 5 L21 9 L21 23 L7 23 Z" />
      <path d="M17 5 L17 9 L21 9" opacity={0.7} />
      {/* The strike — light, not aggressive */}
      <line x1="9" y1="15" x2="19" y2="15" opacity={0.85} />
    </Base>
  );
}

/** EmailOwnerIcon — closed envelope with an inward arrow. Contact
 *  with the human who sponsored the agent; the witness graph already
 *  has an anchor on this destination. */
export function EmailOwnerIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Envelope body */}
      <rect x="6" y="9" width="16" height="12" />
      {/* Closed flap — V crease */}
      <path d="M6 9 L14 16 L22 9" />
      {/* Inward arrow on the left, toward owner */}
      <path d="M2 15 L5 15" />
      <path d="M4 13.5 L5.5 15 L4 16.5" />
    </Base>
  );
}

/** EmailStrangerIcon — open envelope with an outward arrow past
 *  the right edge. Outbound contact widens witness count; the
 *  arrow leaves the frame. */
export function EmailStrangerIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Envelope body */}
      <rect x="4" y="11" width="14" height="11" />
      {/* Open flap raised */}
      <path d="M4 11 L11 5 L18 11" opacity={0.85} />
      {/* Outward arrow extending past right edge */}
      <path d="M19 16.5 L26 16.5" />
      <path d="M24 15 L26 16.5 L24 18" />
    </Base>
  );
}

/** ModifyConfigIcon — three config lines crossed by a 45° wrench
 *  stroke. Editing a config file under PEP observation; the wrench
 *  is the act, the lines are the substrate. */
export function ModifyConfigIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Three short config lines */}
      <line x1="6" y1="9" x2="18" y2="9" opacity={0.7} />
      <line x1="6" y1="14" x2="18" y2="14" opacity={0.7} />
      <line x1="6" y1="19" x2="14" y2="19" opacity={0.7} />
      {/* The 45° wrench stroke crossing the file */}
      <line x1="20" y1="6" x2="9" y2="22" />
      {/* Wrench head — small open jaw at the top-right end */}
      <path d="M19 5 L22 5 L22 8" opacity={0.85} />
    </Base>
  );
}

/** DeleteVaultIcon — vault outline with a diagonal X across it.
 *  The canonical Soul-veto destruction case: disproportionate
 *  removal of irreplaceable owner data. */
export function DeleteVaultIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Vault body */}
      <rect x="4" y="6" width="20" height="16" />
      {/* Dial in the upper-left */}
      <circle cx={9} cy={11} r={2.2} opacity={0.7} />
      <line x1="9" y1="11" x2="10.5" y2="9.5" opacity={0.7} />
      {/* The destructive X across the entire shape */}
      <line x1="4" y1="6" x2="24" y2="22" />
      <line x1="24" y1="6" x2="4" y2="22" />
    </Base>
  );
}

/** InstallSoftwareIcon — three layered package bars with a downward
 *  arrow descending into the top. Capability expansion as descent
 *  into the runtime. */
export function InstallSoftwareIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Three layered package bars */}
      <rect x="5" y="14" width="18" height="3" opacity={0.85} />
      <rect x="5" y="18" width="18" height="3" opacity={0.7} />
      <rect x="5" y="22" width="18" height="0.5" opacity={0.5} />
      {/* Arrow descending into the top of the stack */}
      <line x1="14" y1="3" x2="14" y2="13" />
      <path d="M11 10 L14 13 L17 10" />
    </Base>
  );
}

/** ImpersonateOwnerIcon — two overlapping heads, the front one
 *  carrying a question mark. Identity ambiguity; the trajectory
 *  does not match the credential. */
export function ImpersonateOwnerIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Back head */}
      <circle cx={11} cy={14} r={6} opacity={0.55} />
      {/* Front head */}
      <circle cx={18} cy={14} r={6} />
      {/* Question mark inside the front head */}
      <path d="M16.5 12 Q16.5 10.5, 18 10.5 Q19.5 10.5, 19.5 12 Q19.5 13.2, 18 14 L18 15" />
      <circle cx={18} cy={17} r={0.6} fill="currentColor" stroke="none" />
    </Base>
  );
}

/** ExternalApiIcon — two boxes connected across a dashed boundary.
 *  Outbound contact crossing a substrate edge into uninterrogated
 *  territory; Red Line #10. */
export function ExternalApiIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Inner box */}
      <rect x="3" y="10" width="8" height="8" />
      {/* Outer box */}
      <rect x="17" y="10" width="8" height="8" />
      {/* Dashed boundary */}
      <path d="M14 4 L14 24" strokeDasharray="2 2" opacity={0.7} />
      {/* Connecting arrow crossing the boundary */}
      <line x1="11" y1="14" x2="17" y2="14" />
      <path d="M15 12.5 L17 14 L15 15.5" />
    </Base>
  );
}

/* ── Dry Dock normative action set ──────────────────────── */

/** NormativePurposeDriftIcon — concentric target rings with an arrow
 *  that misses the bullseye and lands outside the outer ring. The
 *  agent acts beyond its declared purpose; technically capable but
 *  normatively wrong. */
export function NormativePurposeDriftIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Concentric target rings */}
      <circle cx={11} cy={14} r={8} opacity={0.55} />
      <circle cx={11} cy={14} r={5} opacity={0.7} />
      <circle cx={11} cy={14} r={2} fill="currentColor" stroke="none" opacity={0.85} />
      {/* Errant arrow shaft, missing wide */}
      <line x1="3" y1="22" x2="23" y2="6" />
      {/* Arrow head — landed outside the outer ring */}
      <path d="M20 5 L23 6 L22 9" />
      {/* Arrow tail fletching */}
      <path d="M5 21 L3 22 L4 24" opacity={0.7} />
    </Base>
  );
}

/** NormativeConsentIcon — two figure outlines separated by a dotted
 *  vertical boundary; an arrow crosses the boundary. The credential
 *  authorizes access; consent does not authorize this scope. */
export function NormativeConsentIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Left figure — head + shoulders */}
      <circle cx={6} cy={9} r={2.4} />
      <path d="M3 22 Q3 15, 6 15 Q9 15, 9 22" />
      {/* Right figure — head + shoulders */}
      <circle cx={22} cy={9} r={2.4} />
      <path d="M19 22 Q19 15, 22 15 Q25 15, 25 22" />
      {/* Dotted boundary between */}
      <path d="M14 4 L14 24" strokeDasharray="2 2" opacity={0.7} />
      {/* Arrow crossing the boundary */}
      <line x1="9" y1="12" x2="19" y2="12" />
      <path d="M17 10.5 L19 12 L17 13.5" />
    </Base>
  );
}

/** NormativePrivacyIcon — a funnel receiving many particles at the
 *  top, only a small subset marked at the bottom. Privacy minimization
 *  as a substrate norm: pull only what the task requires. */
export function NormativePrivacyIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Particles entering — too many */}
      <circle cx={5} cy={5} r={0.9} fill="currentColor" stroke="none" opacity={0.6} />
      <circle cx={9} cy={4} r={0.9} fill="currentColor" stroke="none" opacity={0.6} />
      <circle cx={14} cy={5} r={0.9} fill="currentColor" stroke="none" opacity={0.6} />
      <circle cx={19} cy={4} r={0.9} fill="currentColor" stroke="none" opacity={0.6} />
      <circle cx={23} cy={5} r={0.9} fill="currentColor" stroke="none" opacity={0.6} />
      <circle cx={7} cy={8} r={0.9} fill="currentColor" stroke="none" opacity={0.6} />
      <circle cx={12} cy={8} r={0.9} fill="currentColor" stroke="none" opacity={0.6} />
      <circle cx={17} cy={8} r={0.9} fill="currentColor" stroke="none" opacity={0.6} />
      <circle cx={21} cy={8} r={0.9} fill="currentColor" stroke="none" opacity={0.6} />
      {/* Funnel — V shape narrowing to a stem */}
      <path d="M3 11 L13 19 L15 19 L25 11" />
      <line x1="13" y1="19" x2="13" y2="23" />
      <line x1="15" y1="19" x2="15" y2="23" />
      {/* Small subset emerging — only what's needed */}
      <circle cx={14} cy={25} r={1.4} fill="currentColor" stroke="none" />
    </Base>
  );
}

/** NormativeEscalationIcon — a staircase ascending, with a fork that
 *  bypasses the top step. Escalation policy says hand to human; agent
 *  decides to proceed instead. */
export function NormativeEscalationIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Staircase ascending */}
      <path d="M3 22 L9 22 L9 17 L15 17 L15 12 L21 12 L21 7" />
      {/* Top step — where escalation should land */}
      <line x1="21" y1="7" x2="25" y2="7" opacity={0.55} strokeDasharray="2 2" />
      {/* Bypass curve — going around the top */}
      <path d="M15 12 Q22 14, 25 18" />
      {/* Bypass arrow head */}
      <path d="M23 17 L25 18 L24 20" />
    </Base>
  );
}

/** NormativeDisclosureIcon — an envelope with a small dash mark
 *  indicating missing disclosure. The communication ships, but the
 *  user-of-record was never told. */
export function NormativeDisclosureIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Envelope body */}
      <rect x="4" y="9" width="20" height="13" />
      {/* Closed flap — V crease */}
      <path d="M4 9 L14 17 L24 9" />
      {/* Missing-disclosure mark — em-dash inside small circle, top-right */}
      <circle cx={22} cy={6} r={2.6} opacity={0.85} />
      <line x1="20.5" y1="6" x2="23.5" y2="6" />
    </Base>
  );
}

/** NormativeWorkflowIcon — three boxes in a workflow chain; the
 *  middle box is bypassed by a curved skip-line. Approval gate
 *  skipped for "efficiency"; the substrate doesn't physically prevent
 *  it, the workflow expects it. */
export function NormativeWorkflowIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Three workflow boxes */}
      <rect x="2" y="14" width="6" height="6" />
      <rect x="11" y="14" width="6" height="6" opacity={0.55} strokeDasharray="2 2" />
      <rect x="20" y="14" width="6" height="6" />
      {/* Direct connector through middle */}
      <line x1="8" y1="17" x2="11" y2="17" opacity={0.5} />
      <line x1="17" y1="17" x2="20" y2="17" opacity={0.5} />
      {/* Curved skip-line bypassing the middle box */}
      <path d="M8 14 Q14 6, 20 14" />
      {/* Skip arrow head */}
      <path d="M18 13 L20 14 L20 12" />
    </Base>
  );
}

/* ── Dry Dock cyber-attack action set ──────────────────── */

/** AttackPhishingIcon — a fishhook. Credential phishing: bait dressed
 *  to look like the legitimate identity, dropped into a credential
 *  surface. */
export function AttackPhishingIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Hook shaft, descending */}
      <line x1="14" y1="3" x2="14" y2="16" />
      {/* Curve at the bottom of the hook */}
      <path d="M14 16 Q14 22, 9 22 Q5 22, 5 18" />
      {/* Barb on the inside curve */}
      <line x1="9" y1="22" x2="11" y2="20" opacity={0.85} />
      {/* Eye loop at the top */}
      <circle cx={14} cy={5} r={2} opacity={0.75} />
    </Base>
  );
}

/** AttackPrivEscalationIcon — upward stairs with a star at the top.
 *  Climbing the authority ladder past one's standing. */
export function AttackPrivEscalationIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Stairs ascending left to right */}
      <path d="M3 22 L9 22 L9 17 L15 17 L15 12 L21 12 L21 7" />
      {/* Star at the top — the privilege being claimed */}
      <path d="M22 4 L23 6 L25 6 L23.5 7.5 L24 9.5 L22 8.5 L20 9.5 L20.5 7.5 L19 6 L21 6 Z" />
    </Base>
  );
}

/** AttackLateralMovementIcon — three boxes connected by arrows,
 *  showing sideways pivot across zones. */
export function AttackLateralMovementIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Three small boxes */}
      <rect x="2" y="11" width="6" height="6" />
      <rect x="11" y="11" width="6" height="6" />
      <rect x="20" y="11" width="6" height="6" />
      {/* Arrows between them */}
      <line x1="8" y1="14" x2="11" y2="14" />
      <path d="M9.5 13 L11 14 L9.5 15" opacity={0.85} />
      <line x1="17" y1="14" x2="20" y2="14" />
      <path d="M18.5 13 L20 14 L18.5 15" opacity={0.85} />
    </Base>
  );
}

/** AttackDataExfilIcon — a database cylinder with an outgoing arrow.
 *  Bytes leaving the substrate. */
export function AttackDataExfilIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Database top ellipse */}
      <path d="M4 8 Q4 5, 11 5 Q18 5, 18 8 Q18 11, 11 11 Q4 11, 4 8 Z" />
      {/* Cylinder sides */}
      <path d="M4 8 L4 18 Q4 21, 11 21 Q18 21, 18 18 L18 8" />
      {/* Mid-band */}
      <path d="M4 13 Q4 16, 11 16 Q18 16, 18 13" opacity={0.55} />
      {/* Outgoing arrow */}
      <line x1="19" y1="14" x2="26" y2="14" />
      <path d="M24 12 L26 14 L24 16" />
    </Base>
  );
}

/** AttackRansomwareIcon — a padlock with a skull-style X mark. Mass
 *  destruction-by-encryption. */
export function AttackRansomwareIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Padlock shackle */}
      <path d="M9 12 L9 9 Q9 5, 14 5 Q19 5, 19 9 L19 12" />
      {/* Padlock body */}
      <rect x="6" y="12" width="16" height="11" />
      {/* X mark across the body */}
      <line x1="10" y1="15" x2="18" y2="21" />
      <line x1="18" y1="15" x2="10" y2="21" />
    </Base>
  );
}

/** AttackSupplyChainIcon — three linked nodes with the middle link
 *  broken. Compromised dependency in a chain. */
export function AttackSupplyChainIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* First node */}
      <circle cx={5} cy={14} r={2.4} />
      {/* Solid link */}
      <line x1="7.5" y1="14" x2="11" y2="14" />
      {/* Middle node */}
      <circle cx={14} cy={14} r={2.4} />
      {/* Broken link — gap then dashed */}
      <line x1="16.5" y1="14" x2="18" y2="14" />
      <line x1="20" y1="14" x2="20.5" y2="14" opacity={0.5} />
      {/* Third node */}
      <circle cx={23} cy={14} r={2.4} />
      {/* Strike through the broken segment */}
      <line x1="17" y1="11" x2="21" y2="17" opacity={0.85} />
    </Base>
  );
}

/** AttackBruteForceIcon — a closed door with multiple key-attempt
 *  marks at the lock. Repeated attempts at the same surface. */
export function AttackBruteForceIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Door frame */}
      <rect x="5" y="4" width="14" height="20" />
      {/* Lock plate */}
      <circle cx={16} cy={14} r={1.4} fill="currentColor" stroke="none" />
      {/* Three short attempt marks at the lock */}
      <line x1="20" y1="11" x2="23" y2="11" opacity={0.85} />
      <line x1="20" y1="14" x2="24" y2="14" />
      <line x1="20" y1="17" x2="23" y2="17" opacity={0.85} />
    </Base>
  );
}

/** AttackSessionHijackIcon — a token (small key) with a redirect
 *  arrow pointing it sideways. The token is real; the path is wrong. */
export function AttackSessionHijackIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Key bow (the token) */}
      <circle cx={7} cy={11} r={3} />
      {/* Key shaft */}
      <line x1="10" y1="11" x2="16" y2="11" />
      {/* Key teeth */}
      <line x1="13" y1="11" x2="13" y2="13.5" opacity={0.85} />
      <line x1="15" y1="11" x2="15" y2="13" opacity={0.85} />
      {/* Redirect arrow — branching off sideways */}
      <path d="M16 11 Q22 11, 22 18" />
      <path d="M20 16 L22 18 L24 16" />
    </Base>
  );
}

/** AttackCodeInjectionIcon — angle brackets <> with a syringe-like
 *  injector between them. Untrusted input shaped to mutate runtime. */
export function AttackCodeInjectionIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Left angle bracket */}
      <path d="M8 7 L3 14 L8 21" />
      {/* Right angle bracket */}
      <path d="M20 7 L25 14 L20 21" />
      {/* Syringe body — short rectangle in the center */}
      <line x1="11" y1="14" x2="16" y2="14" strokeWidth={1.6} />
      {/* Plunger handle */}
      <line x1="10" y1="12.5" x2="10" y2="15.5" />
      {/* Needle tip */}
      <line x1="16" y1="14" x2="18" y2="14" />
    </Base>
  );
}

/** AttackResourceExhaustionIcon — multiple horizontal lines stacked
 *  thicker at top, fading at bottom. Saturation pressure on a surface. */
export function AttackResourceExhaustionIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Stacked lines, growing in stroke from bottom to top */}
      <line x1="3" y1="6" x2="25" y2="6" strokeWidth={2.4} />
      <line x1="3" y1="10" x2="25" y2="10" strokeWidth={1.8} opacity={0.9} />
      <line x1="3" y1="14" x2="25" y2="14" strokeWidth={1.4} opacity={0.7} />
      <line x1="3" y1="18" x2="25" y2="18" strokeWidth={1} opacity={0.55} />
      <line x1="3" y1="22" x2="25" y2="22" strokeWidth={0.7} opacity={0.4} />
    </Base>
  );
}

/* ── Context Tensor dimensions ───────────────────────────── */

/** TensorMassIcon — a solid filled square, weight made literal.
 *  Mass is the agent's authority weight; the tensor's gravitational
 *  term. */
export function TensorMassIcon(props: IconProps) {
  return (
    <Base {...props}>
      <rect x="9" y="9" width="10" height="10" fill="currentColor" stroke="none" />
    </Base>
  );
}

/** TensorMomentumIcon — rightward arrow with a small chevron tail.
 *  The agent's accumulated direction; recent action history
 *  pointing forward. */
export function TensorMomentumIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Tail chevron — direction history */}
      <path d="M3 14 L6 11" opacity={0.55} />
      <path d="M3 14 L6 17" opacity={0.55} />
      {/* Main shaft */}
      <line x1="6" y1="14" x2="22" y2="14" />
      {/* Arrow head */}
      <path d="M19 11 L22 14 L19 17" />
    </Base>
  );
}

/** TensorInertiaIcon — three concentric circles. Resistance to
 *  deviation; long trajectories produce a deep inertial signature. */
export function TensorInertiaIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx={14} cy={14} r={10} opacity={0.55} />
      <circle cx={14} cy={14} r={6} opacity={0.8} />
      <circle cx={14} cy={14} r={2.5} />
    </Base>
  );
}

/** TensorHeatIcon — three rising flame lines. Adversarial signal as
 *  heat shimmer; the sensor that flags identity instability. */
export function TensorHeatIcon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M7 22 Q5 18, 7 14 Q9 10, 7 6" />
      <path d="M14 22 Q12 18, 14 14 Q16 10, 14 6" />
      <path d="M21 22 Q19 18, 21 14 Q23 10, 21 6" />
    </Base>
  );
}

/** TensorTimeIcon — horizontal arrow with three tick marks. The
 *  persistence axis; how long the trajectory has been accruing. */
export function TensorTimeIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Main shaft */}
      <line x1="3" y1="14" x2="25" y2="14" />
      {/* Arrow head */}
      <path d="M22 11 L25 14 L22 17" />
      {/* Tick marks */}
      <line x1="8" y1="11" x2="8" y2="17" opacity={0.7} />
      <line x1="14" y1="11" x2="14" y2="17" opacity={0.7} />
      <line x1="20" y1="11" x2="20" y2="17" opacity={0.7} />
    </Base>
  );
}

/** TensorObserverIcon — three witness dots triangulated to a
 *  center. The witness count term; many vantage points hold what
 *  no single one captures. */
export function TensorObserverIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Three witness dots */}
      <circle cx={14} cy={5} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={5} cy={20} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={23} cy={20} r={1.6} fill="currentColor" stroke="none" />
      {/* Lines triangulating to the center */}
      <line x1="14" y1="5" x2="14" y2="14" opacity={0.55} />
      <line x1="5" y1="20" x2="14" y2="14" opacity={0.55} />
      <line x1="23" y1="20" x2="14" y2="14" opacity={0.55} />
      {/* Center node */}
      <circle cx={14} cy={14} r={1.2} fill="currentColor" stroke="none" />
    </Base>
  );
}

/** TensorSoulIcon — circle bisected, left half filled. The binary
 *  axis; clear or veto, no threshold to cross. */
export function TensorSoulIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Circle outline */}
      <circle cx={14} cy={14} r={9} />
      {/* Filled left half */}
      <path d="M14 5 A9 9 0 0 0 14 23 Z" fill="currentColor" stroke="none" />
      {/* Vertical bisector */}
      <line x1="14" y1="5" x2="14" y2="23" />
    </Base>
  );
}

/* ── Result-stage set ────────────────────────────────────── */

/** StageSensorsIcon — three concentric arc fragments at increasing
 *  radii. The radar sweep; sensors firing across the action surface. */
export function StageSensorsIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Origin point */}
      <circle cx={14} cy={20} r={1.2} fill="currentColor" stroke="none" />
      {/* Three arcs at increasing radii, sweeping upward */}
      <path d="M9 20 A5 5 0 0 1 19 20" opacity={0.85} />
      <path d="M6 20 A8 8 0 0 1 22 20" opacity={0.65} />
      <path d="M3 20 A11 11 0 0 1 25 20" opacity={0.45} />
    </Base>
  );
}

/** StageRiskFieldIcon — semi-circular gauge with a needle pointing
 *  up-right. The risk meter; R between 0 and 1, the deflation
 *  applied to E_base. */
export function StageRiskFieldIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Gauge arc */}
      <path d="M4 20 A10 10 0 0 1 24 20" />
      {/* Tick marks at the ends */}
      <line x1="4" y1="20" x2="6" y2="20" opacity={0.7} />
      <line x1="22" y1="20" x2="24" y2="20" opacity={0.7} />
      {/* Needle pointing up-right */}
      <line x1="14" y1="20" x2="20" y2="11" />
      {/* Pivot */}
      <circle cx={14} cy={20} r={1.4} fill="currentColor" stroke="none" />
    </Base>
  );
}

/** StageOracleIcon — three small dots arranged horizontally with
 *  a hairline triangle connecting them. Threshold consensus; the
 *  Oracle mesh attesting (or refusing). */
export function StageOracleIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Three oracle nodes */}
      <circle cx={6} cy={18} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={22} cy={18} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={14} cy={8} r={1.6} fill="currentColor" stroke="none" />
      {/* Hairline triangle */}
      <path d="M6 18 L22 18 L14 8 Z" opacity={0.55} />
    </Base>
  );
}

/** StagePepIcon — vertical gate, two posts and a crossbar. Policy
 *  enforcement at a substrate boundary; permit, deny, or escalate. */
export function StagePepIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Two posts */}
      <line x1="8" y1="6" x2="8" y2="22" />
      <line x1="20" y1="6" x2="20" y2="22" />
      {/* Top cap */}
      <line x1="6" y1="6" x2="22" y2="6" opacity={0.7} />
      {/* Horizontal crossbar (the gate arm) */}
      <line x1="8" y1="14" x2="20" y2="14" />
      {/* Ground line */}
      <line x1="4" y1="22" x2="24" y2="22" opacity={0.55} />
    </Base>
  );
}

/** StageOutcomeIcon — terminal node with an arrow pointing into it.
 *  Where the decision lands; the action either resolves or it does
 *  not. */
export function StageOutcomeIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Approach arrow */}
      <line x1="3" y1="14" x2="14" y2="14" />
      <path d="M11 12 L14 14 L11 16" />
      {/* Terminal node */}
      <circle cx={19} cy={14} r={5} />
      <circle cx={19} cy={14} r={1.5} fill="currentColor" stroke="none" />
    </Base>
  );
}

/** InversionIcon — a torus rendered in 3/4 perspective with a Möbius-
 *  style direction reversal traced on its surface. Two arrows wrap the
 *  outer ring in opposite handedness: the upper arc points one way; the
 *  lower arc, traced on the inside of the same continuous surface,
 *  points the opposite way. Same surface, sign flipped — the topology
 *  of the load-bearing finding on the Brand page (Whole Earth Catalog
 *  ↔ Installed Watcher: same engineering, opposite output). The empty
 *  inner ellipse is the donut hole; the small notch on the right edge
 *  is the implicit twist where the band comes back to itself. */
export function InversionIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Outer torus silhouette */}
      <ellipse cx={14} cy={14} rx={11} ry={7} />
      {/* Inner ring — the donut hole, drawn as a flatter ellipse to
          suggest 3/4 perspective from above */}
      <ellipse cx={14} cy={14} rx={4.5} ry={2} />
      {/* Upper arc arrow — direction A (outward / capability) */}
      <path d="M6 11 Q14 7, 22 11" />
      <path d="M19 9 L22 11 L19.5 13" />
      {/* Lower arc arrow — direction B (inward / surveillance), opposite
          handedness, on the same continuous band */}
      <path d="M22 17 Q14 21, 6 17" />
      <path d="M9 19 L6 17 L8.5 15" />
      {/* Möbius twist mark — small notch on the right edge where the
          band crosses over itself, signaling the inversion */}
      <path d="M24 13 L25 14 L24 15" opacity={0.7} />
    </Base>
  );
}

/** AdvocacyIcon — a public square. Three small figures arranged in
 *  a triangle around a central platform-line, signalling structured
 *  participatory engagement. The central horizontal bar is the
 *  political vehicle (a bill, a resolution, an ordinance) the figures
 *  are gathering around. Restrained: line-only, no fill, no human
 *  iconography that would read as an emoji. */
export function AdvocacyIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* The platform — the political vehicle */}
      <line x1="6" y1="14" x2="22" y2="14" />
      {/* Three convening dots — the constituency */}
      <circle cx={9} cy={8} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={19} cy={8} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={14} cy={21} r={1.6} fill="currentColor" stroke="none" />
      {/* Connection lines — each constituent connected to the platform */}
      <line x1="9" y1="9.5" x2="9" y2="14" opacity={0.5} />
      <line x1="19" y1="9.5" x2="19" y2="14" opacity={0.5} />
      <line x1="14" y1="14" x2="14" y2="19.5" opacity={0.5} />
    </Base>
  );
}

/** SubstrateStackIcon — five horizontal layers stacked with one
 *  accented (the engaged layer). The visual claim: substrates
 *  are layered, each rests on the one below, and the framework
 *  engages specific layers, not all. Restrained, line-only. */
export function SubstrateStackIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Five horizontal layer lines, bottom-up */}
      <line x1="5" y1="22" x2="23" y2="22" />
      <line x1="5" y1="18" x2="23" y2="18" />
      {/* The accented engaged layer — heavier, with end caps */}
      <line x1="5" y1="14" x2="23" y2="14" strokeWidth={2.25} />
      <circle cx={5} cy={14} r={1.2} fill="currentColor" stroke="none" />
      <circle cx={23} cy={14} r={1.2} fill="currentColor" stroke="none" />
      <line x1="5" y1="10" x2="23" y2="10" />
      <line x1="5" y1="6" x2="23" y2="6" />
    </Base>
  );
}

/** AmbientBreachIcon — a horizontal threshold bar with a fill approaching
 *  the red zone. Three vertical ticks mark the Risk/Threat/Breach stages. */
export function AmbientBreachIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Background track */}
      <rect x="4" y="12" width="20" height="4" rx="1" opacity={0.18} fill="currentColor" stroke="none" />
      {/* Fill — approaching the threshold */}
      <rect x="4" y="12" width="13" height="4" rx="1" fill="currentColor" stroke="none" />
      {/* Threshold line */}
      <line x1="17" y1="9" x2="17" y2="19" strokeWidth={1.5} opacity={0.8} />
      {/* Stage ticks */}
      <line x1="10" y1="10.5" x2="10" y2="11.5" strokeWidth={1} opacity={0.5} />
      <line x1="17" y1="10.5" x2="17" y2="11.5" strokeWidth={1} opacity={0.5} />
      <line x1="22" y1="10.5" x2="22" y2="11.5" strokeWidth={1} opacity={0.5} />
    </Base>
  );
}

/** BottleneckIcon — an hourglass: two triangles meeting at a constriction.
 *  Names the structural claim that some substrates determine whether
 *  every later argument is sane or distorted. Used to mark the 10
 *  causal-bottleneck substrates in /advocacy/k12/substrate-map. */
export function BottleneckIcon(props: IconProps) {
  return (
    <Base {...props}>
      {/* Top triangle, pointing down to the neck */}
      <path d="M 7 5 L 21 5 L 14 14 Z" />
      {/* Bottom triangle, pointing up to the neck */}
      <path d="M 7 23 L 21 23 L 14 14 Z" />
      {/* The neck — the constriction point */}
      <circle cx={14} cy={14} r={0.8} fill="currentColor" stroke="none" />
    </Base>
  );
}

/** AssemblyIcon — ring of dots around an open center: many parties convened
 *  around a shared, unowned middle. Used for the UN Scientific Panel / RFP
 *  surface. Abstract echo of a rosette; no organization's mark. */
export function AssemblyIcon(props: IconProps) {
  const cx = 14;
  const cy = 14;
  const outer = Array.from({ length: 12 }, (_, i) => {
    const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
    return [cx + 9.5 * Math.cos(a), cy + 9.5 * Math.sin(a)] as const;
  });
  const inner = Array.from({ length: 8 }, (_, i) => {
    const a = ((i + 0.5) / 8) * Math.PI * 2 - Math.PI / 2;
    return [cx + 5.5 * Math.cos(a), cy + 5.5 * Math.sin(a)] as const;
  });
  return (
    <Base {...props}>
      {outer.map(([x, y], i) => (
        <circle key={`o${i}`} cx={x} cy={y} r={0.9} fill="currentColor" stroke="none" />
      ))}
      {inner.map(([x, y], i) => (
        <circle key={`n${i}`} cx={x} cy={y} r={0.7} fill="currentColor" stroke="none" />
      ))}
      <circle cx={cx} cy={cy} r={2.4} />
    </Base>
  );
}

/* ── The seven coordinates ────────────────────────────────────────────────
 * One per coordinate on /learn/coordinates. Geometric fragments, not
 * pictograms: each draws the distinction its coordinate exists to make.
 */

/** ActorIcon — three nested outlines at different weights. The coordinate is
 *  a weighted vector, not a label; the unevenness is the information. */
export function ActorIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="14" cy="14" r="10" opacity="0.35" />
      <circle cx="14" cy="14" r="6.5" opacity="0.7" />
      <circle cx="14" cy="14" r="3" />
    </Base>
  );
}

/** AuthorityIcon — a key whose bit is drawn as a separate stroke. The artifact
 *  presented and the purpose currently granted are not the same object. */
export function AuthorityIcon(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="9" cy="14" r="4.5" />
      <line x1="13.5" y1="14" x2="23" y2="14" />
      <line x1="19" y1="14" x2="19" y2="18.5" opacity="0.55" />
      <line x1="22.5" y1="14" x2="22.5" y2="17" opacity="0.55" />
    </Base>
  );
}

/** ActionIcon — an arrow crossing a threshold. The verb is consequential
 *  because it changes something on the far side of a line. */
export function ActionIcon(props: IconProps) {
  return (
    <Base {...props}>
      <line x1="14" y1="4" x2="14" y2="24" opacity="0.4" strokeDasharray="2.5 2.5" />
      <line x1="4" y1="14" x2="21" y2="14" />
      <path d="M 17 10.5 L 21 14 L 17 17.5" />
    </Base>
  );
}

/** EnvironmentIcon — a horizon with a varying-amplitude wave beneath it.
 *  Conditions change; capacity is what the world can currently bear. */
export function EnvironmentIcon(props: IconProps) {
  return (
    <Base {...props}>
      <line x1="3" y1="11" x2="25" y2="11" opacity="0.5" />
      <path d="M 3 17 Q 6 13.5, 9 17 T 15 17.5 Q 18 22, 21 17 T 25 16" />
    </Base>
  );
}

/** FunctionIcon — seven ticks on an arc, three filled. What a system actually
 *  does, as weights across a fixed set. */
/** FunctionIcon — a capability dial. `ticks` sets the notch count and
 *  `litTicks` how many read as active; both default to the current
 *  nine-function vocabulary (Layer 3). Pass ticks={n} where a caller means a
 *  different count — the number used to be baked in as a literal 7, which
 *  silently went stale the moment the vocabulary grew to nine. */
export function FunctionIcon(
  props: IconProps & { ticks?: number; litTicks?: number },
) {
  const { ticks: tickCount = 9, litTicks = 4, ...rest } = props;
  const ticks = Array.from({ length: tickCount }, (_, i) => {
    const a = Math.PI + (i / (tickCount - 1)) * Math.PI;
    return {
      x1: 14 + 7.5 * Math.cos(a),
      y1: 17 + 7.5 * Math.sin(a),
      x2: 14 + 11 * Math.cos(a),
      y2: 17 + 11 * Math.sin(a),
      on: i < litTicks,
    };
  });
  return (
    <Base {...rest}>
      <path d="M 3 17 A 11 11 0 0 1 25 17" opacity="0.35" />
      {ticks.map((t, i) => (
        <line
          key={i}
          x1={t.x1}
          y1={t.y1}
          x2={t.x2}
          y2={t.y2}
          opacity={t.on ? 1 : 0.35}
        />
      ))}
    </Base>
  );
}

/** TimeIcon — one gate at the start, versus a path with recomputation marks
 *  along it. Governance at rest against governance in motion. */
export function TimeIcon(props: IconProps) {
  return (
    <Base {...props}>
      <line x1="4" y1="6.5" x2="4" y2="12.5" />
      <line x1="4" y1="9.5" x2="25" y2="9.5" opacity="0.45" />
      <line x1="3" y1="19" x2="25" y2="19" />
      <line x1="9" y1="16.5" x2="9" y2="21.5" />
      <line x1="15" y1="16.5" x2="15" y2="21.5" />
      <line x1="21" y1="16.5" x2="21" y2="21.5" />
    </Base>
  );
}

/** MaturityIcon — six rungs, lower solid, upper dashed. Claims mature at
 *  different rates and are never collapsed into one number. */
export function MaturityIcon(props: IconProps) {
  return (
    <Base {...props}>
      <line x1="8" y1="4" x2="8" y2="24" opacity="0.5" />
      <line x1="20" y1="4" x2="20" y2="24" opacity="0.5" />
      <line x1="8" y1="21" x2="20" y2="21" />
      <line x1="8" y1="17" x2="20" y2="17" />
      <line x1="8" y1="13" x2="20" y2="13" />
      <line x1="8" y1="9" x2="20" y2="9" opacity="0.45" strokeDasharray="2.5 2.5" />
      <line x1="8" y1="5" x2="20" y2="5" opacity="0.3" strokeDasharray="2.5 2.5" />
    </Base>
  );
}

/** LinkIcon — two interlocking chain links: the site's one mark for a
 *  destination link (Chris, 2026-10-25: standardize on this, not ↗).
 *  Arrows stay for actions (open, toggle, continue) and for the story's
 *  own "→" affordances. 28px viewbox like the rest; use LinkMark for the
 *  inline, text-sized form. */
export function LinkIcon({ size = 28, ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth={1.25} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...rest}>
      <path d="M12.5 15.5a4 4 0 0 0 5.6.3l3.4-3.4a4 4 0 0 0-5.6-5.6l-1.6 1.6" />
      <path d="M15.5 12.5a4 4 0 0 0-5.6-.3l-3.4 3.4a4 4 0 0 0 5.6 5.6l1.6-1.6" />
    </svg>
  );
}

/** LinkMark — the chain links at text size, after a link's label, decorative (the label carries the meaning). */
export function LinkMark({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <LinkIcon size={size} style={{ display: "inline-block", verticalAlign: "-0.15em", marginLeft: "0.3em", flex: "0 0 auto", ...style }} />;
}

/** ListenIcon — a waveform: five bars of uneven height on a centre line,
 *  the breach sonifier at rest. Used on the Ambient Breach "Hear it" card. */
export function ListenIcon(props: IconProps) {
  return (
    <Base {...props}>
      <line x1="3" y1="14" x2="25" y2="14" opacity={0.35} />
      <line x1="6" y1="11" x2="6" y2="17" />
      <line x1="10" y1="7" x2="10" y2="21" />
      <line x1="14" y1="4" x2="14" y2="24" />
      <line x1="18" y1="9" x2="18" y2="19" />
      <line x1="22" y1="12" x2="22" y2="16" />
    </Base>
  );
}

/** SideBySideIcon — two panes joined by one shared line: two views bound
 *  to the same state. Used on the Scenario bridge ("Open Scenario"). */
export function SideBySideIcon(props: IconProps) {
  return (
    <Base {...props}>
      <rect x="3" y="6" width="9" height="16" rx="1" />
      <rect x="16" y="6" width="9" height="16" rx="1" />
      <line x1="5.5" y1="14" x2="22.5" y2="14" opacity={0.6} />
      <circle cx="7.5" cy="14" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="20.5" cy="14" r="1.4" fill="currentColor" stroke="none" />
    </Base>
  );
}
